import { createClient, type Session, type SupabaseClient, type User } from '@supabase/supabase-js';

const SUPABASE_CLIENT_KEY = '__landiCanvasSupabase';

type GlobalWithSupabase = typeof globalThis & {
  [SUPABASE_CLIENT_KEY]?: SupabaseClient;
};

function createSupabaseClient(url: string, anonKey: string): SupabaseClient {
  return createClient(url, anonKey, {
    auth: {
      flowType: 'pkce',
      // Callback exchange is explicit in AuthCallbackPage — avoid racing
      // detectSessionInUrl against exchangeCodeForSession on /auth/callback.
      detectSessionInUrl: false,
      persistSession: true,
      autoRefreshToken: true,
      storage: typeof window !== 'undefined' ? window.localStorage : undefined,
    },
  });
}

export function isSupabaseConfigured(): boolean {
  return Boolean(import.meta.env.VITE_SUPABASE_URL && import.meta.env.VITE_SUPABASE_ANON_KEY);
}

export function getSupabaseClient(): SupabaseClient | null {
  const url = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  if (!url || !anonKey) {
    return null;
  }
  const globalRef = globalThis as GlobalWithSupabase;
  if (!globalRef[SUPABASE_CLIENT_KEY]) {
    globalRef[SUPABASE_CLIENT_KEY] = createSupabaseClient(url, anonKey);
  }
  return globalRef[SUPABASE_CLIENT_KEY] ?? null;
}

/** Test-only reset for unit tests. */
export function resetSupabaseClientForTests(): void {
  const globalRef = globalThis as GlobalWithSupabase;
  delete globalRef[SUPABASE_CLIENT_KEY];
}

export type { Session, User };
